import { useEffect, useState } from 'react';
import { BeakerIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { geneticsApi } from '../../api/genetics';
import { speciesApi, type Species } from '../../api/species';

interface MutationInventoryReportProps {
  speciesId?: string;
}

export default function MutationInventoryReport({ speciesId: initialSpeciesId }: MutationInventoryReportProps) {
  const [speciesList, setSpeciesList] = useState<Species[]>([]);
  const [speciesId, setSpeciesId] = useState(initialSpeciesId ?? '');
  const [data, setData] = useState<Awaited<ReturnType<typeof geneticsApi.getMutationInventoryReport>> | null>(null);
  const [splitCarriers, setSplitCarriers] = useState<Awaited<ReturnType<typeof geneticsApi.getSplitCarriersReport>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    speciesApi.getAll().then((res) => setSpeciesList(res.data)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!speciesId) {
      setData(null);
      setSplitCarriers(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [result, splits] = await Promise.all([
          geneticsApi.getMutationInventoryReport(speciesId),
          geneticsApi.getSplitCarriersReport(speciesId),
        ]);
        if (!cancelled) {
          setData(result);
          setSplitCarriers(splits);
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load mutation inventory');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [speciesId]);

  return (
    <div className="space-y-4">
      <div className="bg-white shadow rounded-lg p-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Species</label>
        <select
          value={speciesId}
          onChange={(e) => setSpeciesId(e.target.value)}
          className="block w-full max-w-md rounded-md border-gray-300 text-sm"
        >
          <option value="">Select a species…</option>
          {speciesList.map((sp) => (
            <option key={sp.id} value={sp.id}>{sp.commonName}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="bg-white shadow rounded-lg p-6 text-center text-sm text-slate-600">Loading…</div>
      )}

      {error && (
        <div className="bg-white shadow rounded-lg p-6 text-center text-sm text-red-600">{error}</div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <BeakerIcon className="h-5 w-5 text-indigo-600" />
                Total birds
              </div>
              <p className="text-2xl font-semibold text-slate-900 mt-1">{data.totalBirds}</p>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <ChartBarIcon className="h-5 w-5 text-green-600" />
                With structured genotypes
              </div>
              <p className="text-2xl font-semibold text-slate-900 mt-1">{data.birdsWithStructuredGenotypes}</p>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <p className="text-sm text-slate-500">Catalog loci</p>
              <p className="text-2xl font-semibold text-slate-900 mt-1">{data.loci.length}</p>
            </div>
          </div>

          {data.loci.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-6 text-sm text-slate-600">
              No mutation catalog for {data.speciesName}. Seed a reference catalog on the species Genetics tab.
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Locus</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Wild type</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Visual</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Split</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Hemizygous</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.loci.map((locus) => (
                    <tr key={locus.locusId}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900">{locus.locusName}</span>
                        <span className="ml-2 font-mono text-xs text-slate-500">({locus.locusSymbol})</span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">{locus.wildTypeCount}</td>
                      <td className="px-4 py-3 text-right text-purple-700 font-medium">{locus.visualCount}</td>
                      <td className="px-4 py-3 text-right text-indigo-700">{locus.splitCount}</td>
                      <td className="px-4 py-3 text-right text-pink-700">{locus.hemizygousCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {splitCarriers && splitCarriers.totalSplitCarriers > 0 && (
            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Split carriers ({splitCarriers.totalSplitCarriers} birds)
              </h3>
              <div className="space-y-3">
                {splitCarriers.byLocus.map((locus) => (
                  <div key={locus.locusId}>
                    <p className="text-xs font-medium text-indigo-800">
                      {locus.locusName} ({locus.locusSymbol})
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {locus.carriers.map((c) => c.bandId || c.birdId.slice(0, 8)).join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
